import React from 'react';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import './ReusableFontPicker.css';

export interface FontOption {
  value: string;
  label: string;
}

interface ReusableFontPickerProps {
  value: string | undefined;
  options: FontOption[];
  onChange: (nextFont: string) => void;
  className?: string;
}

const ReusableFontPicker: React.FC<ReusableFontPickerProps> = ({
  value,
  options,
  onChange,
  className = '',
}) => {
  const resolvedOptions: FontOption[] = [...options];
  if (
    value &&
    !resolvedOptions.some((fontOption: FontOption) => fontOption.value === value)
  ) {
    resolvedOptions.unshift({ value: value, label: value });
  }

  const selectedOption: FontOption | null = value
    ? (resolvedOptions.find(
        (fontOption: FontOption) => fontOption.value === value
      ) ?? { value: value, label: value })
    : null;

  return (
    <Tooltip
      title={selectedOption?.label ?? ''}
      placement="top"
      arrow
      disableHoverListener={!selectedOption?.label}
    >
      <Autocomplete<FontOption, false, false, false>
        className={`font-picker-autocomplete ${className}`.trim()}
        fullWidth
        disablePortal
        options={resolvedOptions}
        value={selectedOption}
        isOptionEqualToValue={(option: FontOption, optionValue: FontOption) =>
          option.value === optionValue.value
        }
        getOptionLabel={(option: FontOption) => option.label}
        onChange={(_, nextOption: FontOption | null) => {
          if (nextOption) {
            onChange(nextOption.value);
          }
        }}
        renderOption={(props, option) => (
          <li {...props} style={{ fontFamily: option.value }}>
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              sx: {
                overflow: 'hidden',
                '& .MuiAutocomplete-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            }}
          />
        )}
      />
    </Tooltip>
  );
};

export default ReusableFontPicker;
